import { AlertTriangle, Archive, Check, CheckSquare, ExternalLink, FileText, Inbox, LayoutGrid, List, Mail, MailX, Maximize2, Mic, Paperclip, Pencil, RefreshCw, RotateCcw, Send, Sparkles, Trash2, Undo2, X, Mailbox } from "lucide-react";
import { DateField } from "@/components/DateField";
import { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";

import { api } from "@/api";
import { ThemeStats } from "@/components/theme/ThemeStats";
import { AddColumn, ColumnTools } from "@/components/inbox/ColumnTools";
import { PeriodLoader } from "@/components/inbox/PeriodLoader";
import { InboxListView } from "@/components/InboxListView";
import { VoiceInstructionPanel } from "@/components/VoiceInstructionPanel";
import "@/inbox.css";

const COLUMNS = [
  { key: "pret", title: "Prêt à envoyer", subtitle: "Tout est déjà préparé", color: "col-green" },
  { key: "a_traiter", title: "À traiter manuellement", subtitle: "Préparation auto impossible", color: "col-orange" },
  { key: "mail_non_envoye", title: "Mail non envoyé", subtitle: "Adresse manquante ou invalide", color: "col-red" },
  { key: "envoye", title: "Envoyé — attente du contact", subtitle: "", color: "col-purple" },
  { key: "refuse", title: "Refusés / Sans suite", subtitle: "Offre déclinée — fiche annulée", color: "col-gray" },
  { key: "termine", title: "Terminé", subtitle: "", color: "col-gray" },
  { key: "auto", title: "Automatiques", subtitle: "DMARC, noreply, absences, newsletters : aucune réponse proposée", color: "col-gray" },
];

function timeAgo(value) {
  if (!value) return "";
  const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
  if (minutes < 60) return `il y a ${Math.max(minutes, 1)} min`;
  if (minutes < 1440) return `il y a ${Math.round(minutes / 60)} h`;
  return `le ${new Date(value).toLocaleDateString("fr-FR")}`;
}

function fullDate(value) {
  if (!value) return "";
  return new Date(value).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

function buildTimeline(ticket) {
  const received = (ticket.messages && ticket.messages.length
    ? ticket.messages
    : ticket.body_text
      ? [{ received_at: ticket.received_at, from_email: ticket.from_email, subject: ticket.subject, body_text: ticket.body_text }]
      : []
  ).map((m) => ({ kind: "received", at: m.received_at, from: m.from_email, subject: m.subject, body: m.body_text }));
  const sent = (ticket.sent_log || []).map((s) => ({ kind: "sent", at: s.sent_at, to: s.to, subject: s.subject, body: s.body, human: s.human, with_quote: s.with_quote, gmail_id: s.gmail_id }));
  const sentTimes = new Set(sent.map((s) => s.at));
  const humans = (ticket.human_replies || [])
    .filter((h) => !sentTimes.has(h.sent_at))
    .map((h) => ({ kind: "sent", at: h.sent_at, to: h.to, subject: h.subject, body: h.body, human: true, gmail_id: h.gmail_id }));
  return [...received, ...sent, ...humans].sort((a, b) => new Date(a.at || 0) - new Date(b.at || 0));
}

export default function InboxPage() {
  const [tickets, setTickets] = useState(null);
  const [stats, setStats] = useState({});
  const [lastSync, setLastSync] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [gmailError, setGmailError] = useState(null);
  const [validatingId, setValidatingId] = useState(null);
  const [updatingEmailId, setUpdatingEmailId] = useState(null);
  const [signingId, setSigningId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ reply_subject: "", reply_body: "" });
  const [expandedId, setExpandedId] = useState(null);
  const [gmailAccount, setGmailAccount] = useState(null);
  const [mailbox, setMailbox] = useState(() => localStorage.getItem("inbox-mailbox") || "");
  const [boxes, setBoxes] = useState(null);
  const mailboxRef = useRef(mailbox);
  mailboxRef.current = mailbox;
  const readOnly = mailbox.startsWith("archive:");
  const [columns, setColumns] = useState(COLUMNS);
  useEffect(() => { api.get("/inbox/columns", { params: { mailbox: mailbox || undefined } }).then(({ data }) => setColumns(data)).catch(() => {}); }, [mailbox]);
  const saveColumns = async (next) => {
    try { const { data } = await api.put("/inbox/columns", { columns: next }, { params: { mailbox: mailbox || undefined } }); setColumns(data); return true; } catch (e) { toast.error(e.response?.data?.detail || "Modification impossible."); return false; }
  };
  const chooseMailbox = (value) => { localStorage.setItem("inbox-mailbox", value); setMailbox(value); };
  useEffect(() => { api.get("/inbox/mailboxes").then(({ data }) => setBoxes(data)).catch(() => {}); }, []);
  useEffect(() => { loadTickets().catch(() => {}); }, [mailbox]); // eslint-disable-line react-hooks/exhaustive-deps
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkMoving, setBulkMoving] = useState(false);
  const [reorientId, setReorientId] = useState(null);
  const [rechecking, setRechecking] = useState(false);
  const [recheckProgress, setRecheckProgress] = useState(null);

  const revertReply = async (ticketId) => {
    try {
      const { data } = await api.post(`/inbox/tickets/${ticketId}/revert-reply`);
      toast.success(data.message);
      await loadTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "La restauration a échoué.");
    }
  };
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("inbox-view") || "kanban");
  const [sortDir, setSortDir] = useState("desc");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [listStatus, setListStatus] = useState("all");

  const switchView = (mode) => {
    setViewMode(mode);
    localStorage.setItem("inbox-view", mode);
  };

  const inRange = (ticket) => {
    const day = (ticket.received_at || ticket.created_at || "").slice(0, 10);
    if (dateFrom && day < dateFrom) return false;
    if (dateTo && day > dateTo) return false;
    return true;
  };

  const visibleColumns = columns.filter((c) => !c.hidden || (stats[c.key] || 0) > 0);
  const visibleTickets = (tickets || [])
    .filter(inRange)
    .sort((a, b) => {
      const diff = new Date(a.received_at || 0) - new Date(b.received_at || 0);
      return sortDir === "desc" ? -diff : diff;
    });

  const listTickets = visibleTickets.filter((ticket) => listStatus === "all" || ticket.status === listStatus || (listStatus === "pret" && ticket.status === "preparation"));
  const hasDateFilter = Boolean(dateFrom || dateTo);

  const toggleSelected = (ticketId) => {
    setSelectedIds((current) => (current.includes(ticketId) ? current.filter((id) => id !== ticketId) : [...current, ticketId]));
  };

  const toggleColumnSelection = (columnTickets) => {
    const ids = columnTickets.map((ticket) => ticket.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.includes(id));
    setSelectedIds((current) => (allSelected ? current.filter((id) => !ids.includes(id)) : [...new Set([...current, ...ids])]));
  };

  const bulkMove = async (status) => {
    const target = columns.find((column) => column.key === status);
    setBulkMoving(true);
    try {
      const { data } = await api.patch("/inbox/tickets/bulk-status", { ticket_ids: selectedIds, status });
      toast.success(`${data.moved} ticket${data.moved > 1 ? "s" : ""} déplacé${data.moved > 1 ? "s" : ""} vers « ${target.title} ».`);
      setSelectedIds([]);
      await loadTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Le déplacement groupé a échoué.");
    } finally {
      setBulkMoving(false);
    }
  };

  const gmailUrl = (id, account = gmailAccount) => (account ? `https://mail.google.com/mail/?authuser=${encodeURIComponent(account)}#all/${id}` : `https://mail.google.com/mail/u/0/#all/${id}`);

  const loadTickets = async () => {
    const { data } = await api.get("/inbox/tickets", { params: { mailbox: mailboxRef.current || undefined } });
    setTickets(data.tickets);
    setStats(data.stats);
    setLastSync(data.last_sync_at);
    setGmailAccount(data.gmail_account);
    return data;
  };

  const refreshing_ = useRef(false);
  const refresh = async (silent = false, period = null) => {
    if (refreshing_.current) return;
    refreshing_.current = true;
    setRefreshing(true);
    try {
      const { data } = await api.post("/inbox/refresh", period || {});
      setGmailError(null);
      if (!silent) toast.success(data.message);
      if (data.background) [45000, 90000, 150000].forEach((ms) => setTimeout(() => loadTickets().catch(() => {}), ms));
    } catch (error) {
      const detail = error.response?.data?.detail || "Le relevé Gmail a échoué.";
      setGmailError(detail);
      if (!silent) toast.error(detail);
    } finally {
      refreshing_.current = false;
      setRefreshing(false);
      await loadTickets().catch(() => {});
    }
  };

  useEffect(() => {
    // affichage immédiat depuis notre base ; relevé Gmail seulement si le dernier date de plus de 2 min (le serveur relève déjà tout seul)
    loadTickets().then((d) => { if (!d.last_sync_at || Date.now() - new Date(d.last_sync_at).getTime() > 120000) refresh(true); }).catch(() => toast.error("Impossible de charger les réponses."));
    const timer = setInterval(() => {
      refresh(true);
    }, 5 * 60 * 1000);
    const lightTimer = setInterval(() => loadTickets().catch(() => {}), 30000);
    return () => {
      clearInterval(timer);
      clearInterval(lightTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recheckRunning = recheckProgress && recheckProgress.status === "en_cours";

  useEffect(() => {
    loadRecheckProgress();
  }, []);

  useEffect(() => {
    if (!recheckRunning) return undefined;
    const timer = setInterval(() => {
      loadRecheckProgress();
      loadTickets().catch(() => {});
    }, 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recheckRunning]);

  const validate = async (ticket) => {
    setValidatingId(ticket.id);
    try {
      const { data } = await api.post(`/inbox/tickets/${ticket.id}/validate`);
      toast.success(data.message);
      if (data.email_update_error) {
        toast.warning("La réponse est partie, mais l’adresse n’a pas pu être mise à jour dans noCRM. Utilisez « Mettre à jour l’adresse dans noCRM ».");
      }
      await loadTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "L’envoi a échoué.");
    } finally {
      setValidatingId(null);
    }
  };

  const [uploadingId, setUploadingId] = useState(null);
  const [techSheets, setTechSheets] = useState([]);

  useEffect(() => {
    api.get("/inbox/technical-sheets").then((r) => setTechSheets(r.data.sheets || [])).catch(() => {});
  }, []);

  const toggleTechnicalSheet = async (ticket, index, attach) => {
    try {
      const { data } = await api.patch(`/inbox/tickets/${ticket.id}/technical-sheet`, { attach, index });
      toast.success(data.message);
      await loadTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action impossible.");
    }
  };

  const sheetSelected = (ticket, index) => {
    const indices = ticket.technical_sheet_indices ?? (ticket.attach_technical_sheet ? techSheets.map((s) => s.index) : []);
    return indices.includes(index);
  };

  const uploadAttachment = async (ticketId, input) => {
    const file = input.files?.[0];
    if (!file) return;
    setUploadingId(ticketId);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(`/inbox/tickets/${ticketId}/attachments`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.message);
      await loadTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Envoi de la pièce jointe impossible.");
    } finally {
      setUploadingId(null);
      input.value = "";
    }
  };

  const removeAttachment = async (ticketId, attachmentId) => {
    try {
      await api.delete(`/inbox/tickets/${ticketId}/attachments/${encodeURIComponent(attachmentId)}`);
      await loadTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Suppression impossible.");
    }
  };

  const updateContactEmail = async (ticket) => {
    setUpdatingEmailId(ticket.id);
    try {
      const { data } = await api.post(`/inbox/tickets/${ticket.id}/update-contact-email`, {});
      toast.success(data.message);
      await loadTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "La mise à jour de l’adresse a échoué.");
    } finally {
      setUpdatingEmailId(null);
    }
  };

  const loadRecheckProgress = async () => {
    try {
      const { data } = await api.get("/inbox/reanalyze-contact-emails/progress");
      setRecheckProgress(data.progress);
    } catch {
      /* silencieux */
    }
  };

  const startRecheck = async () => {
    setRechecking(true);
    try {
      const { data } = await api.post("/inbox/reanalyze-contact-emails");
      toast.success(data.message);
      await loadRecheckProgress();
    } catch (error) {
      toast.error(error.response?.data?.detail || "La réanalyse n’a pas pu démarrer.");
    } finally {
      setRechecking(false);
    }
  };

  const markSigned = async (ticket) => {
    setSigningId(ticket.id);
    try {
      const { data } = await api.post(`/inbox/tickets/${ticket.id}/mark-signed`);
      toast.success(data.message);
      await loadTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Le passage en « Devis signé » a échoué.");
    } finally {
      setSigningId(null);
    }
  };

  const moveTicket = async (ticketId, status) => {
    try {
      await api.patch(`/inbox/tickets/${ticketId}`, { status });
      await loadTickets();
    } catch {
      toast.error("Le déplacement a échoué.");
    }
  };

  const [deletingId, setDeletingId] = useState(null);
  const deleteTicket = async (ticket) => {
    setDeletingId(ticket.id);
    try {
      const { data } = await api.delete(`/inbox/tickets/${ticket.id}`);
      toast.success(data.message);
      setSelectedIds((current) => current.filter((id) => id !== ticket.id));
      await loadTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "La suppression a échoué.");
    } finally {
      setDeletingId(null);
    }
  };

  const [trashOpen, setTrashOpen] = useState(false);
  const [trash, setTrash] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const loadTrash = async () => {
    setTrashLoading(true);
    try {
      const { data } = await api.get("/inbox/trash");
      setTrash(data.tickets || []);
    } catch {
      toast.error("Impossible de charger la corbeille.");
    } finally {
      setTrashLoading(false);
    }
  };
  const openTrash = async () => {
    setTrashOpen(true);
    await loadTrash();
  };
  const restoreTicket = async (ticket) => {
    try {
      const { data } = await api.post(`/inbox/tickets/${ticket.id}/restore`);
      toast.success(data.message);
      await Promise.all([loadTrash(), loadTickets()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "La restauration a échoué.");
    }
  };
  const purgeTicket = async (ticket) => {
    if (!window.confirm(`Supprimer DÉFINITIVEMENT ce ticket${ticket.commune_name ? ` (${ticket.commune_name})` : ""} ? Il ne pourra plus être récupéré.`)) return;
    try {
      const { data } = await api.delete(`/inbox/tickets/${ticket.id}/purge`);
      toast.success(data.message);
      await loadTrash();
    } catch (error) {
      toast.error(error.response?.data?.detail || "La suppression définitive a échoué.");
    }
  };

  const [focusMailKo, setFocusMailKo] = useState(false);
  const [retryEmails, setRetryEmails] = useState({});
  const [retryingId, setRetryingId] = useState(null);
  const retryMailKo = async (ticket) => {
    const email = (retryEmails[ticket.id] || "").trim();
    if (!email) {
      toast.error("Saisissez l'adresse e-mail corrigée.");
      return;
    }
    setRetryingId(ticket.id);
    try {
      const { data } = await api.post(`/inbox/tickets/${ticket.id}/retry-mail`, { email });
      toast.success(data.message);
      setRetryEmails((current) => {
        const next = { ...current };
        delete next[ticket.id];
        return next;
      });
      await loadTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "La relance a échoué.");
    } finally {
      setRetryingId(null);
    }
  };

  const [composingId, setComposingId] = useState(null);
  const [focusedId, setFocusedId] = useState(null);
  const fullLoaded = useRef(new Set());
  const [fullBodies, setFullBodies] = useState({});
  const loadFullBodies = (ticketId) => {
    if (fullLoaded.current.has(ticketId)) return;
    fullLoaded.current.add(ticketId);
    api.get(`/inbox/tickets/${ticketId}/bodies`).then(({ data }) => setFullBodies((m) => ({ ...m, [ticketId]: data }))).catch(() => fullLoaded.current.delete(ticketId));
  };
  const withFull = (t) => {
    const full = fullBodies[t.id];
    if (!full) return t;
    return {
      ...t,
      body_text: full.body_text,
      messages: (t.messages || []).map((m, i) => ({ ...m, body_text: full.messages[i] ?? m.body_text })),
      sent_log: (t.sent_log || []).map((x, i) => ({ ...x, body: full.sent_log[i] ?? x.body })),
    };
  };
  useEffect(() => { if (focusedId) loadFullBodies(focusedId); }, [focusedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const maybeFocus = (event, ticketId) => {
    if (event.target.closest('button, a, input, textarea, select, label, .message-text, .mic-inline, [role="button"]')) return;
    setFocusedId(ticketId);
  };

  useEffect(() => {
    const onKey = (event) => { if (event.key === "Escape") setFocusedId(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const composeReply = async (ticket, mode) => {
    setComposingId(ticket.id);
    try {
      const { data } = await api.post(`/inbox/tickets/${ticket.id}/compose`, { mode });
      toast.success(data.message);
      await loadTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "La préparation de la réponse a échoué.");
    } finally {
      setComposingId(null);
    }
  };

  const cancelReply = async (ticket) => {
    try {
      await api.post(`/inbox/tickets/${ticket.id}/cancel-reply`);
      setEditingId((current) => (current === ticket.id ? null : current));
      setReorientId((current) => (current === ticket.id ? null : current));
      await loadTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "L’annulation a échoué.");
    }
  };

  const saveDraft = async (ticketId) => {
    try {
      await api.patch(`/inbox/tickets/${ticketId}`, draft);
      toast.success("Réponse mise à jour.");
      setEditingId(null);
      await loadTickets();
    } catch {
      toast.error("L’enregistrement a échoué.");
    }
  };

  if (!tickets) return <section className="page">Chargement des réponses…</section>;

  return (
    <section className="page inbox-page" data-testid="inbox-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">RÉPONSES</p>
          <h1>Réponses reçues</h1>
          <p className="page-subtitle">
            Les e-mails reçus sur Gmail, rattachés au bon dossier, analysés par l’IA, devis corrigé et réponse déjà préparés.
            {lastSync ? ` Dernier relevé : ${timeAgo(lastSync)}.` : ""}
          </p>
        </div>
        {boxes && <ThemeStats stats={[{ icon: Inbox, value: tickets.length, label: "E-mails dans cette vue", testid: "inbox-head-count" }, { icon: Mailbox, value: boxes.all, label: "Toutes les boîtes", tone: "pine", testid: "inbox-head-all" }]} />}
        <div className="inbox-header-actions">
          {boxes && (
            <select className="inbox-mailbox" value={mailbox || boxes.accounts.find((a) => a.is_primary)?.id || ""} onChange={(e) => chooseMailbox(e.target.value)} data-testid="inbox-mailbox-select">
              {boxes.accounts.map((a) => <option key={a.id} value={a.id}>{`${a.email} (${a.count})`}</option>)}
              {boxes.archives.map((a) => <option key={a.id} value={`archive:${a.id}`}>{`Archive · ${a.name} (${a.count})`}</option>)}
              <option value="all">{`Toutes les boîtes (${boxes.all})`}</option>
            </select>
          )}
          <button className="secondary-button" onClick={openTrash} data-testid="inbox-trash-button" title="Voir les tickets supprimés (restaurables 30 jours)">
            <Trash2 size={16} /> Corbeille
          </button>
          <button className="secondary-button" onClick={startRecheck} disabled={rechecking || recheckRunning} data-testid="inbox-recheck-button" title="Re-détecter les changements d’adresse sur les anciens tickets, mettre à jour noCRM et renvoyer le dernier devis">
            <Sparkles size={16} />{rechecking ? "Lancement…" : recheckRunning ? "Réanalyse en cours…" : "Réanalyser les anciens tickets"}
          </button>
          <PeriodLoader busy={refreshing} onLoad={(period) => refresh(false, period)} />
          <button className="primary-button" onClick={() => refresh(false)} disabled={refreshing} data-testid="inbox-refresh-button">
            <RefreshCw size={17} className={refreshing ? "spinning" : ""} />{refreshing ? "Relevé en cours…" : "Actualiser"}
          </button>
        </div>
      </header>

      {readOnly && <div className="inbox-archive-banner" data-testid="inbox-archive-banner"><Archive size={16} />Archive en consultation : tout l'historique de l'ancienne boîte est conservé ici. Les tickets ne peuvent plus être modifiés ni envoyés.</div>}
      {recheckProgress && recheckProgress.status === "en_cours" && (
        <div className="retry-progress" data-testid="inbox-recheck-banner">
          <div className="retry-progress-track"><div className="retry-progress-fill" style={{ width: `${recheckProgress.total ? Math.round((recheckProgress.done / recheckProgress.total) * 100) : 0}%` }} /></div>
          <p data-testid="inbox-recheck-text">
            Réanalyse des changements d’adresse : <strong>{recheckProgress.done}/{recheckProgress.total}</strong> tickets — {recheckProgress.updated} adresse(s) mise(s) à jour, {recheckProgress.resent} devis renvoyé(s) · mise à jour automatique
          </p>
        </div>
      )}
      {recheckProgress && recheckProgress.status === "termine" && recheckProgress.finished_at && Date.now() - new Date(recheckProgress.finished_at).getTime() < 15 * 60 * 1000 && (
        <p className="retry-progress-done" data-testid="inbox-recheck-done">
          Réanalyse terminée : {recheckProgress.updated} adresse(s) mise(s) à jour dans noCRM et {recheckProgress.resent} devis renvoyé(s) à la nouvelle adresse (sur {recheckProgress.total} tickets analysés).
        </p>
      )}
      {recheckProgress && recheckProgress.status === "interrompu" && (
        <p className="review-alert" data-testid="inbox-recheck-interrupted">
          La réanalyse a été interrompue à {recheckProgress.done}/{recheckProgress.total}. Relancez-la pour traiter les tickets restants.
        </p>
      )}

      {gmailError && (
        <p className="review-alert" data-testid="inbox-gmail-error">
          {gmailError} {gmailError.includes("reconnectez") || gmailError.includes("lecture") ? <a href="/gmail">Ouvrir la page Gmail</a> : null}
        </p>
      )}

      <div className="inbox-toolbar" data-testid="inbox-toolbar">
        <div className="view-switch" role="tablist" aria-label="Mode d’affichage">
          <button type="button" className={viewMode === "kanban" ? "active" : ""} onClick={() => switchView("kanban")} data-testid="inbox-view-kanban">
            <LayoutGrid size={14} /> Kanban
          </button>
          <button type="button" className={viewMode === "list" ? "active" : ""} onClick={() => switchView("list")} data-testid="inbox-view-list">
            <List size={14} /> Liste
          </button>
        </div>
        <div className="date-filter">
          <label>Du <DateField value={dateFrom} max={dateTo || undefined} onChange={setDateFrom} placeholder="Début" testid="inbox-date-from" /></label>
          <label>au <DateField value={dateTo} min={dateFrom || undefined} onChange={setDateTo} placeholder="Fin" testid="inbox-date-to" /></label>
          {hasDateFilter && (
            <button type="button" className="link-button" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="inbox-date-clear">
              <X size={12} /> Effacer
            </button>
          )}
        </div>
        {viewMode === "list" && (
          <select className="status-filter" value={listStatus} onChange={(event) => setListStatus(event.target.value)} aria-label="Filtrer par statut" data-testid="inbox-list-status-filter">
            <option value="all">Tous les statuts</option>
            {visibleColumns.map((column) => <option key={column.key} value={column.key}>{column.title}</option>)}
          </select>
        )}
        <span className="table-count" data-testid="inbox-visible-count">
          {(viewMode === "list" ? listTickets : visibleTickets).length} / {tickets.length} ticket(s){hasDateFilter ? " (filtre actif)" : ""}
        </span>
        {viewMode === "kanban" && (stats.mail_non_envoye || 0) > 0 && (
          <button
            type="button"
            className={`mailko-filter-chip${focusMailKo ? " active" : ""}`}
            onClick={() => setFocusMailKo((current) => !current)}
            data-testid="inbox-mailko-filter"
            title="Afficher uniquement les fiches à corriger d’adresse et les relancer"
          >
            <MailX size={14} /> Mail non envoyé ({stats.mail_non_envoye || 0}){focusMailKo ? " · tout afficher" : ""}
          </button>
        )}
      </div>

      {viewMode === "list" && (
        <InboxListView
          tickets={listTickets}
          selectedIds={selectedIds}
          onToggle={toggleSelected}
          onToggleAll={toggleColumnSelection}
          sortDir={sortDir}
          onToggleSort={() => setSortDir((current) => (current === "desc" ? "asc" : "desc"))}
          gmailUrl={gmailUrl}
          columns={visibleColumns}
          onMove={moveTicket}
        />
      )}

      {viewMode === "kanban" && <div className={`kanban-board${focusMailKo ? " kanban-board-focus" : ""}`} data-testid="kanban-board">
        {(focusMailKo ? visibleColumns.filter((column) => column.key === "mail_non_envoye") : visibleColumns).map((column) => {
          const columnTickets = visibleTickets.filter((ticket) => ticket.status === column.key || (column.key === "pret" && ticket.status === "preparation"));
          return (
            <div
              key={column.key}
              className={`kanban-column ${column.color}${columnTickets.length === 0 && !focusMailKo ? " is-collapsed" : ""}`}
              title={columnTickets.length === 0 ? `${column.title} : aucun ticket (déposez une carte ici)` : undefined}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                const ticketId = event.dataTransfer.getData("ticketId");
                if (ticketId) moveTicket(ticketId, column.key);
              }}
              data-testid={`kanban-column-${column.key}`}
            >
              <div className="kanban-column-head">
                <div className="kanban-column-title">
                  <h3>{column.title} ({hasDateFilter ? columnTickets.length : stats[column.key] || 0})</h3>
                  {!readOnly && <ColumnTools column={column} count={stats[column.key] || 0} columns={columns} onSave={saveColumns} />}
                  {columnTickets.length > 0 && (
                    <label className="select-all" title="Tout sélectionner dans cette colonne">
                      <input
                        type="checkbox"
                        checked={columnTickets.every((ticket) => selectedIds.includes(ticket.id))}
                        onChange={() => toggleColumnSelection(columnTickets)}
                        data-testid={`select-all-${column.key}`}
                      />
                      Tout
                    </label>
                  )}
                </div>
                {column.subtitle && <p>{column.subtitle}</p>}
              </div>
              {columnTickets.length === 0 && <p className="kanban-empty">Aucun ticket</p>}
              {columnTickets.map((ticket) => {
                const isFocused = focusedId === ticket.id;
                const cardEl = (
                <article
                  key={ticket.id}
                  className={`kanban-card${selectedIds.includes(ticket.id) ? " selected" : ""}${isFocused ? " card-focused" : ""}`}
                  draggable={!isFocused}
                  onDragStart={(event) => event.dataTransfer.setData("ticketId", ticket.id)}
                  onClick={isFocused ? undefined : (event) => maybeFocus(event, ticket.id)}
                  data-testid={`ticket-card-${ticket.id}`}
                >
                  <div className="kanban-card-head">
                    <input
                      type="checkbox"
                      className="ticket-checkbox"
                      checked={selectedIds.includes(ticket.id)}
                      onChange={() => toggleSelected(ticket.id)}
                      aria-label="Sélectionner ce ticket"
                      data-testid={`ticket-select-${ticket.id}`}
                    />
                    <strong>
                      {ticket.lead_url ? (
                        <a href={ticket.lead_url} target="_blank" rel="noreferrer" className="lead-link">{ticket.commune_name || ticket.from_email} <ExternalLink size={11} /></a>
                      ) : (ticket.commune_name || ticket.from_email)}
                    </strong>
                    {!ticket.treatment_id && <span className="ticket-category" data-testid={`ticket-category-${ticket.id}`}>{ticket.ai_category || "e-mail"}</span>}
                    <span className="kanban-time">{timeAgo(ticket.received_at)}</span>
                    {!isFocused && (
                      <button type="button" className="ticket-expand" onClick={() => setFocusedId(ticket.id)} title="Agrandir cette carte" aria-label="Agrandir cette carte" data-testid={`ticket-expand-${ticket.id}`}>
                        <Maximize2 size={13} />
                      </button>
                    )}
                    <button type="button" className="ticket-delete" onClick={() => deleteTicket(ticket)} disabled={deletingId === ticket.id} title="Supprimer ce ticket" aria-label="Supprimer ce ticket" data-testid={`ticket-delete-${ticket.id}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                  <p className="kanban-from">
                    De : {ticket.from_email}{ticket.account_email && <span className="gmail-box-chip" data-testid={`ticket-account-${ticket.id}`}> → {ticket.account_email}</span>}
                    {(ticket.thread_id || ticket.gmail_message_id) && (
                      <a className="gmail-link" href={gmailUrl(ticket.thread_id || ticket.gmail_message_id, ticket.account_email || undefined)} target="_blank" rel="noreferrer" data-testid={`ticket-gmail-link-${ticket.id}`}>
                        <Mail size={11} /> Ouvrir dans Gmail
                      </a>
                    )}
                  </p>
                  {ticket.status === "preparation" && <p className="kanban-ai preparing"><Sparkles size={13} /> Analyse IA en cours…</p>}
                  {ticket.status === "mail_non_envoye" && (
                    <div className="mailko-block" data-testid={`ticket-mailko-${ticket.id}`}>
                      <p className="mailko-head"><MailX size={14} /> E-mail non envoyé</p>
                      <p className="mailko-detail">Adresse tentée : <strong>{ticket.mail_error_email || ticket.from_email || "—"}</strong></p>
                      {(ticket.mail_error_reason || ticket.error) && <p className="mailko-reason">{ticket.mail_error_reason || ticket.error}</p>}
                      {ticket.lead_url && <a className="lead-link" href={ticket.lead_url} target="_blank" rel="noreferrer" data-testid={`ticket-mailko-crm-${ticket.id}`}>Ouvrir la fiche noCRM <ExternalLink size={11} /></a>}
                      <div className="mailko-retry" data-testid={`ticket-mailko-retry-${ticket.id}`}>
                        <label className="mailko-retry-label">Corriger l’adresse et relancer le devis :</label>
                        <div className="mailko-retry-row">
                          <input
                            type="email"
                            placeholder="adresse.correcte@exemple.fr"
                            value={retryEmails[ticket.id] || ""}
                            onChange={(event) => setRetryEmails((current) => ({ ...current, [ticket.id]: event.target.value }))}
                            data-testid={`ticket-mailko-input-${ticket.id}`}
                          />
                          <button type="button" disabled={retryingId === ticket.id} onClick={() => retryMailKo(ticket)} data-testid={`ticket-mailko-retry-btn-${ticket.id}`}>
                            <Send size={13} /> {retryingId === ticket.id ? "Relance…" : "Corriger & relancer"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {ticket.ai_summary && <p className="kanban-ai" data-testid={`ticket-ai-${ticket.id}`}><Sparkles size={13} /> {ticket.ai_summary}</p>}
                  {ticket.error && ticket.status !== "mail_non_envoye" && <p className="kanban-error" data-testid={`ticket-error-${ticket.id}`}><AlertTriangle size={13} /> {ticket.error}</p>}
                  {(() => {
                    const timeline = buildTimeline(withFull(ticket));
                    const lastReceivedIdx = timeline.map((e) => e.kind).lastIndexOf("received");
                    if (!timeline.length) return null;
                    return (
                      <div className="conversation-thread" data-testid={`ticket-conversation-${ticket.id}`}>
                        {timeline.map((entry, index) => {
                          const key = `conv-${ticket.id}-${index}`;
                          const open = expandedId === key || (entry.kind === "received" && index === lastReceivedIdx && expandedId === ticket.id);
                          const body = entry.body || "";
                          const isLastReceived = entry.kind === "received" && index === lastReceivedIdx;
                          return (
                            <div key={key} className={`message-block ${entry.kind === "received" ? "message-received" : entry.human ? "message-agent" : "message-reply"}`} data-testid={`ticket-conv-${entry.kind}-${ticket.id}-${index}`}>
                              <span className="message-label message-label-row">
                                {entry.kind === "received"
                                  ? <>📩 Reçu — {fullDate(entry.at)}{entry.from ? ` · De : ${entry.from}` : ""}</>
                                  : <>✉️ {entry.human ? "Envoyé par un agent" : "Réponse envoyée"} — {fullDate(entry.at)}{entry.to ? ` · À : ${entry.to}` : ""}{entry.with_quote ? " · devis joint" : ""}</>}
                                {isLastReceived && (ticket.status === "pret" || ticket.status === "a_traiter") && (
                                  <button type="button" className="mic-inline" onClick={() => setReorientId(reorientId === ticket.id ? null : ticket.id)} title="Réorienter l’IA au micro" aria-label="Réorienter l’IA" data-testid={`ticket-mic-received-${ticket.id}`}>
                                    <Mic size={12} />
                                  </button>
                                )}
                              </span>
                              {entry.subject && entry.kind === "sent" && <p className="message-subject">{entry.subject}</p>}
                              <p className="message-text" onClick={() => { if (!open) loadFullBodies(ticket.id); setExpandedId(open ? null : key); }}>
                                {open ? body : `${body.slice(0, 180)}${body.length > 180 ? "… (cliquer pour tout lire)" : ""}`}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  {ticket.prepared_quote && (
                    <a className="kanban-quote" href={`${api.defaults.baseURL}/inbox/tickets/${ticket.id}/quote.pdf`} target="_blank" rel="noreferrer" data-testid={`ticket-quote-${ticket.id}`}>
                      <FileText size={13} /> Devis {ticket.prepared_quote.number} v{ticket.prepared_quote.version} — {ticket.prepared_quote.total_quantity} clim · {Number(ticket.prepared_quote.total_ttc).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} € TTC — Aperçu
                    </a>
                  )}
                  {ticket.auto_modification_applied && (
                    <p className="kanban-email-done" data-testid={`ticket-modification-applied-${ticket.id}`}>
                      <Check size={15} />
                      <span>✅ Modification appliquée automatiquement : devis régénéré{ticket.modification_total_quantity ? <> (nouveau total : <strong>{ticket.modification_total_quantity} clim</strong>, mis à jour dans noCRM)</> : null}, e-mail envoyé au contact{ticket.moved_to_step ? <>, et fiche déplacée vers « <strong>{ticket.moved_to_step}</strong> »</> : null}. Les relances repartent depuis cette étape.</span>
                    </p>
                  )}
                  {ticket.modification_error && (
                    <p className="review-alert" data-testid={`ticket-modification-error-${ticket.id}`}>
                      ⚠️ La modification automatique a échoué ({ticket.modification_error}). Vérifiez la fiche puis validez manuellement.
                    </p>
                  )}
                  {ticket.latest_quote && (!ticket.prepared_quote || ticket.latest_quote.version > ticket.prepared_quote.version) && (
                    <a className="kanban-quote kanban-quote-latest" href={`${api.defaults.baseURL}/quotes/${ticket.treatment_id}/versions/${ticket.latest_quote.version}/pdf`} target="_blank" rel="noreferrer" data-testid={`ticket-latest-quote-${ticket.id}`}>
                      <FileText size={13} /> Dernier devis du dossier : {ticket.latest_quote.number} v{ticket.latest_quote.version} — {ticket.latest_quote.total_quantity} clim · {Number(ticket.latest_quote.total_ttc).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} € TTC — Aperçu
                    </a>
                  )}
                  {ticket.is_refusal && (ticket.status === "pret" || ticket.status === "a_traiter") && (
                    <p className="kanban-error" data-testid={`ticket-refusal-${ticket.id}`}><span>❌ Refus détecté — à la validation, la fiche noCRM sera déplacée en « Pas intéressé » et passée au statut Annulé</span></p>
                  )}
                  {ticket.is_refusal && ticket.status === "refuse" && (
                    <p className="kanban-email-done" data-testid={`ticket-refused-${ticket.id}`}>
                      <Check size={15} />
                      <span>✅ Refus traité automatiquement : accusé de réception envoyé{ticket.sent_at ? <> {timeAgo(ticket.sent_at)}</> : null}, fiche noCRM passée en « Pas intéressé » (Annulé).</span>
                    </p>
                  )}
                  {ticket.new_contact_email && (ticket.status === "pret" || ticket.status === "a_traiter") && (
                    <div className="kanban-newmail-block" data-testid={`ticket-new-email-${ticket.id}`}>
                      {ticket.contact_email_updated_at ? (
                        <p className="kanban-email-done" data-testid={`ticket-email-updated-${ticket.id}`}>
                          <Check size={15} />
                          <span>✅ Changement d’adresse déjà effectué dans noCRM : la fiche a été mise à jour vers <strong>{ticket.new_contact_email}</strong>
                          {ticket.previous_contact_email && ticket.previous_contact_email !== ticket.new_contact_email ? <> (ancienne : {ticket.previous_contact_email})</> : null}
                          {" "}{timeAgo(ticket.contact_email_updated_at)}.
                          {ticket.quote_resent_at
                            ? <> Le dernier devis a été <strong>renvoyé automatiquement</strong> à cette nouvelle adresse {timeAgo(ticket.quote_resent_at)}.</>
                            : ticket.quote_resend_error
                              ? <> ⚠️ Le renvoi automatique du devis a échoué ({ticket.quote_resend_error}).</>
                              : null}
                          {" "}La réponse partira à cette nouvelle adresse.</span>
                        </p>
                      ) : (
                        <>
                          <p className="kanban-ai kanban-newmail">
                            <span>➡️ Nouvelle adresse de contact détectée : <strong>{ticket.new_contact_email}</strong>
                            {ticket.previous_contact_email && ticket.previous_contact_email !== ticket.new_contact_email ? <> (ancienne : {ticket.previous_contact_email})</> : null}
                            {" "}— {ticket.contact_email_update_error ? "la mise à jour automatique de la fiche noCRM a échoué, cliquez pour réessayer" : "mise à jour de la fiche noCRM en cours…"}</span>
                          </p>
                          <button type="button" className="secondary-button" disabled={updatingEmailId === ticket.id} onClick={() => updateContactEmail(ticket)} data-testid={`ticket-update-email-${ticket.id}`}>
                            {updatingEmailId === ticket.id ? "Mise à jour…" : "Mettre à jour l’adresse dans noCRM"}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                  {ticket.is_signed && (
                    <div className="kanban-signed-block" data-testid={`ticket-signed-${ticket.id}`}>
                      <p className="kanban-signed"><span>📝 Devis signé reçu — Facture attendue</span></p>
                      {ticket.signed_marked_at ? (
                        <p className="kanban-sent" data-testid={`ticket-signed-done-${ticket.id}`}><Check size={13} /> Fiche passée en « Devis signé » {timeAgo(ticket.signed_marked_at)}</p>
                      ) : (
                        <button type="button" className="secondary-button" disabled={signingId === ticket.id} onClick={() => markSigned(ticket)} data-testid={`ticket-mark-signed-${ticket.id}`}>
                          {signingId === ticket.id ? "En cours…" : "Marquer « Devis signé » dans noCRM"}
                        </button>
                      )}
                    </div>
                  )}
                  {(ticket.status === "pret" || ticket.status === "a_traiter" || ticket.reply_open) && (
                    <div className="kanban-attachments" data-testid={`ticket-attachments-${ticket.id}`}>
                      {techSheets.length > 0 && (
                        <div className="tech-sheets" data-testid={`ticket-tech-sheets-${ticket.id}`}>
                          <p className="attach-heading"><FileText size={13} /> Fiches techniques (cochez celle(s) à joindre)</p>
                          {techSheets.map((sheet) => (
                            <div key={sheet.index} className="tech-sheet-row">
                              <label className="attach-toggle" data-testid={`ticket-tech-sheet-label-${ticket.id}-${sheet.index}`}>
                                <input type="checkbox" checked={sheetSelected(ticket, sheet.index)} onChange={(event) => toggleTechnicalSheet(ticket, sheet.index, event.target.checked)} data-testid={`ticket-tech-sheet-${ticket.id}-${sheet.index}`} />
                                Joindre
                              </label>
                              <a className="attach-view-link" href={`${api.defaults.baseURL}/inbox/technical-sheets/${sheet.index}.pdf`} target="_blank" rel="noreferrer" data-testid={`ticket-tech-sheet-view-${ticket.id}-${sheet.index}`}>
                                <ExternalLink size={12} /> {sheet.filename}
                              </a>
                            </div>
                          ))}
                        </div>
                      )}
                      {(ticket.user_attachments || []).map((att) => (
                        <span key={att.id} className="attach-chip" data-testid={`ticket-attachment-${ticket.id}`}>
                          <Paperclip size={12} /> {att.filename}
                          <button type="button" onClick={() => removeAttachment(ticket.id, att.id)} data-testid={`ticket-attachment-remove-${ticket.id}`} aria-label="Retirer la pièce jointe">×</button>
                        </span>
                      ))}
                      <label className="attach-add" data-testid={`ticket-attachment-add-${ticket.id}`}>
                        <Paperclip size={13} /> {uploadingId === ticket.id ? "Ajout en cours…" : "Ajouter une pièce jointe"}
                        <input type="file" hidden disabled={uploadingId === ticket.id} onChange={(event) => uploadAttachment(ticket.id, event.target)} />
                      </label>
                    </div>
                  )}
                  {(ticket.status === "pret" || ticket.status === "a_traiter" || ticket.reply_open) && (
                    <>
                      {ticket.reply_open && (
                        <p className="reply-open-hint" data-testid={`ticket-reply-open-${ticket.id}`}>
                          <Sparkles size={13} /> Réponse en préparation — relisez, modifiez (clavier ou micro) puis envoyez.
                        </p>
                      )}
                      {editingId === ticket.id ? (
                        <div className="kanban-edit">
                          <input value={draft.reply_subject} onChange={(event) => setDraft({ ...draft, reply_subject: event.target.value })} data-testid={`ticket-edit-subject-${ticket.id}`} />
                          <textarea rows={6} value={draft.reply_body} onChange={(event) => setDraft({ ...draft, reply_body: event.target.value })} data-testid={`ticket-edit-body-${ticket.id}`} />
                          <div className="button-row">
                            <button className="secondary-button" type="button" onClick={() => saveDraft(ticket.id)} data-testid={`ticket-save-draft-${ticket.id}`}><Check size={14} />Enregistrer</button>
                            <button className="secondary-button" type="button" onClick={() => setEditingId(null)}>Annuler</button>
                          </div>
                        </div>
                      ) : (
                        <div className="message-block message-reply" data-testid={`ticket-reply-${ticket.id}`}>
                          <span className="message-label">✉️ Réponse proposée</span>
                          <p className="message-text">
                            {ticket.reply_body
                              ? (expandedId === `reply-${ticket.id}` ? ticket.reply_body : `${ticket.reply_body.slice(0, 180)}${ticket.reply_body.length > 180 ? "…" : ""}`)
                              : "(Message vide — cliquez « Modifier » pour le rédiger, ou « Réorienter l’IA » au micro.)"}
                          </p>
                          <div className="button-row reply-actions">
                            {(ticket.reply_body || "").length > 180 && (
                              <button type="button" className="link-button" onClick={() => setExpandedId(expandedId === `reply-${ticket.id}` ? null : `reply-${ticket.id}`)} data-testid={`ticket-expand-reply-${ticket.id}`}>
                                {expandedId === `reply-${ticket.id}` ? "Réduire" : "Tout lire"}
                              </button>
                            )}
                            <button type="button" className="link-button" onClick={() => { setEditingId(ticket.id); setDraft({ reply_subject: ticket.reply_subject || "", reply_body: ticket.reply_body || "" }); }} data-testid={`ticket-edit-${ticket.id}`}>
                              <Pencil size={12} /> Modifier
                            </button>
                            <button type="button" className="link-button reorient-link" onClick={() => setReorientId(reorientId === ticket.id ? null : ticket.id)} data-testid={`ticket-reorient-${ticket.id}`}>
                              <Mic size={12} /> Réorienter l’IA
                            </button>
                            {ticket.reply_history_count > 0 && (
                              <button type="button" className="link-button" onClick={() => revertReply(ticket.id)} title="Restaurer la réponse précédente" data-testid={`ticket-revert-${ticket.id}`}>
                                <RotateCcw size={12} /> Version précédente
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      {reorientId === ticket.id && (
                        <VoiceInstructionPanel ticketId={ticket.id} onRegenerated={async () => { await loadTickets(); setReorientId(null); }} onClose={() => setReorientId(null)} />
                      )}
                      <button className="validate-button" type="button" disabled={validatingId === ticket.id} onClick={() => validate(ticket)} data-testid={`ticket-validate-${ticket.id}`}>
                        <Send size={15} />{validatingId === ticket.id ? "Envoi…" : "Valider & envoyer"}
                      </button>
                      {ticket.reply_open ? (
                        <button type="button" className="link-button cancel-reply-link" onClick={() => cancelReply(ticket)} data-testid={`ticket-cancel-reply-${ticket.id}`}>
                          <X size={12} /> Annuler la réponse
                        </button>
                      ) : (
                        <a className="kanban-adjust" href={`/devis/validation?tid=${ticket.treatment_id}`} data-testid={`ticket-adjust-${ticket.id}`}>Ajuster manuellement</a>
                      )}
                    </>
                  )}
                  {(ticket.status === "envoye" || ticket.status === "termine") && !ticket.reply_open && (
                    <div className="conv-followup" data-testid={`ticket-followup-${ticket.id}`}>
                      {ticket.status === "envoye" && <p className="kanban-sent"><Check size={13} /> Réponse envoyée {timeAgo(ticket.sent_at)} — en attente du contact</p>}
                      {ticket.status === "termine" && ticket.human_replied_at && <p className="kanban-sent" data-testid={`ticket-human-${ticket.id}`}><Check size={13} /> Déjà répondu par un agent {timeAgo(ticket.human_replied_at)}</p>}
                      <div className="followup-actions">
                        <button type="button" className="secondary-button" disabled={composingId === ticket.id} onClick={() => composeReply(ticket, "ai")} data-testid={`ticket-reply-ai-${ticket.id}`}>
                          <Sparkles size={14} /> {composingId === ticket.id ? "Préparation…" : "Répondre (brouillon IA)"}
                        </button>
                        <button type="button" className="link-button" disabled={composingId === ticket.id} onClick={() => composeReply(ticket, "blank")} data-testid={`ticket-reply-blank-${ticket.id}`}>
                          <Pencil size={13} /> Message vierge
                        </button>
                      </div>
                    </div>
                  )}
                </article>
                );
                return isFocused
                  ? createPortal(
                      <div className="card-focus-overlay" onClick={() => setFocusedId(null)} data-testid="card-focus-overlay">
                        <div className="card-focus-inner" onClick={(event) => event.stopPropagation()}>
                          <button type="button" className="card-focus-close" onClick={() => setFocusedId(null)} aria-label="Fermer" data-testid="card-focus-close"><X size={18} /></button>
                          {cardEl}
                        </div>
                      </div>,
                      document.body,
                      ticket.id,
                    )
                  : cardEl;
              })}
            </div>
          );
        })}
        {!readOnly && !focusMailKo && <AddColumn columns={columns} onSave={saveColumns} />}
      </div>}

      {tickets.length === 0 && (
        <p className="source-note inbox-empty" data-testid="inbox-empty">
          <Inbox size={15} /> Aucun e-mail pour le moment. Les e-mails reçus sur les devis envoyés apparaîtront ici automatiquement, analysés et prêts à valider. Utilisez « Charger les e-mails » pour relire une période.
        </p>
      )}

      {selectedIds.length > 0 && createPortal(
        <div className="bulk-bar" data-testid="bulk-action-bar">
          <span className="bulk-count" data-testid="bulk-selected-count">
            <CheckSquare size={16} /> {selectedIds.length} ticket{selectedIds.length > 1 ? "s" : ""} sélectionné{selectedIds.length > 1 ? "s" : ""}
          </span>
          <span className="bulk-label">Déplacer vers :</span>
          {visibleColumns.map((column) => (
            <button
              key={column.key}
              className={`bulk-target ${column.color}`}
              onClick={() => bulkMove(column.key)}
              disabled={bulkMoving}
              data-testid={`bulk-move-${column.key}`}
            >
              {column.title}
            </button>
          ))}
          <button className="bulk-cancel" onClick={() => setSelectedIds([])} disabled={bulkMoving} data-testid="bulk-cancel-button">
            <X size={14} /> Annuler
          </button>
        </div>,
        document.body
      )}

      {trashOpen && createPortal(
        <div className="trash-overlay" onClick={() => setTrashOpen(false)} data-testid="trash-overlay">
          <div className="trash-panel" onClick={(event) => event.stopPropagation()} data-testid="trash-panel">
            <div className="trash-head">
              <h2><Trash2 size={18} /> Corbeille</h2>
              <button type="button" className="trash-close" onClick={() => setTrashOpen(false)} data-testid="trash-close" aria-label="Fermer"><X size={18} /></button>
            </div>
            <p className="trash-sub">Les tickets supprimés restent ici 30 jours puis sont effacés automatiquement. Restaurez-en un en un clic.</p>
            {trashLoading ? (
              <p className="trash-empty">Chargement…</p>
            ) : trash.length === 0 ? (
              <p className="trash-empty" data-testid="trash-empty"><Inbox size={15} /> La corbeille est vide.</p>
            ) : (
              <ul className="trash-list">
                {trash.map((ticket) => (
                  <li key={ticket.id} className="trash-item" data-testid={`trash-item-${ticket.id}`}>
                    <div className="trash-item-info">
                      <strong>{ticket.commune_name || ticket.from_email || "Ticket sans commune"}</strong>
                      <span className="trash-item-meta">
                        {ticket.from_email ? `De : ${ticket.from_email} · ` : ""}Supprimé {timeAgo(ticket.deleted_at)}
                      </span>
                      {(ticket.ai_summary || ticket.mail_error_reason) && <span className="trash-item-summary">{ticket.ai_summary || ticket.mail_error_reason}</span>}
                    </div>
                    <div className="trash-item-actions">
                      <button type="button" className="secondary-button" onClick={() => restoreTicket(ticket)} data-testid={`trash-restore-${ticket.id}`}>
                        <Undo2 size={14} /> Restaurer
                      </button>
                      <button type="button" className="trash-purge" onClick={() => purgeTicket(ticket)} data-testid={`trash-purge-${ticket.id}`} title="Supprimer définitivement">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}
